import { VOUCHER_APIS } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import AxiosCurlirize from "axios-curlirize";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { IVoucherFamilyListResponse, IVoucherFamilyResponseData } from "@/api_framework/api_modals/Membership";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";

AxiosCurlirize(axios, () => {});

export const useMembershipVoucherListingAPI = () => {
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();
  const [voucherList, setVoucherList] = useState<IVoucherFamilyResponseData[]>([]);
  const [isVoucherListInProgress, setIsVoucherListInProgress] = useState<boolean>(false);

  const execute = useCallback(
    async (serviceType?: string, search?: string): Promise<IVoucherFamilyResponseData[]> => {
      // Consumers that pass a serviceType only need the returned data, they don't drive the listing state.
      const shouldTrackState = !serviceType;
      const searchQuery = search?.trim();
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        if (shouldTrackState) setIsVoucherListInProgress(true);
        const response = await axios
          .get(VOUCHER_APIS.VOUCHER_LIST?.baseURL, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
            params: {
              ...(searchQuery ? { name: searchQuery } : {}),
              ...(serviceType ? { service_type: serviceType } : {}),
            },
          })
          .then((res: AxiosResponse<IVoucherFamilyListResponse>) => {
            const data = res.data.status === true ? res.data.data ?? [] : [];
            if (shouldTrackState) setVoucherList(data);
            return data;
          })
          .catch((e: AxiosError) => {
            if (shouldTrackState) setVoucherList([]);
            sendCurl(e.config["curlCommand"]);
            return [] as IVoucherFamilyResponseData[];
          });
        return response;
      } catch (error) {
        if (shouldTrackState) setVoucherList([]);
        if (axios.isAxiosError(error)) {
          captureError(error);
        } else {
          toast.error("Server Error: " + error.message);
        }
        return [] as IVoucherFamilyResponseData[];
      } finally {
        if (shouldTrackState) setIsVoucherListInProgress(false);
      }
    },
    [dispatch],
  );

  return { execute, voucherList, isVoucherListInProgress };
};
